import {
  CollectionReference,
  collection,
  query,
  getDocs,
  doc,
  getDoc,
  DocumentData,
  where,
} from 'firebase/firestore';
import {
  placeOrderFn,
  getPaymentUrl,
  placeBusOrderFn,
  transferTicketFn,
  placeTicketOrderFn,
} from './functions';
import {
  PlaceOrderRequest,
  Collections,
  Order,
  Ticket,
  TicketType,
  PlaceBusOrderRequest,
  Bus,
  BusRoute,
  BusOrder,
  FestivalEdition,
  Event,
  PlaceTicketOrderRequest,
  TicketingOwnerRef,
} from '@packages/types';
import { firestore } from '@/firebase';
import { TransferTicketRequest } from '@packages/types/requestTypes';

type FAQ = { q: string; a: string };

type ActiveFestivalConfig = { activeFestivalEditionId?: string };

export class FirebaseClient {
  private ticketCollection: CollectionReference<Ticket, Ticket>;
  private orderCollection: CollectionReference<Order, Order>;
  private ticketTypeCollection: CollectionReference<TicketType, TicketType>;
  private contentCollection: CollectionReference<DocumentData, DocumentData>;
  private busCollection: CollectionReference<Bus, Bus>;
  private busRouteCollection: CollectionReference<BusRoute, BusRoute>;
  private busOrderCollection: CollectionReference<BusOrder, BusOrder>;
  private festivalEditionCollection: CollectionReference<FestivalEdition, FestivalEdition>;
  private eventCollection: CollectionReference<Event, Event>;

  constructor() {
    this.ticketTypeCollection = collection(
      firestore,
      Collections.ticketTypes,
    ) as CollectionReference<TicketType, TicketType>;

    this.orderCollection = collection(firestore, Collections.orders) as CollectionReference<
      Order,
      Order
    >;

    this.ticketCollection = collection(firestore, Collections.tickets) as CollectionReference<
      Ticket,
      Ticket
    >;
    this.contentCollection = collection(firestore, Collections.content);

    this.busCollection = collection(firestore, Collections.bus) as CollectionReference<Bus, Bus>;
    this.busRouteCollection = collection(firestore, Collections.busRoutes) as CollectionReference<
      BusRoute,
      BusRoute
    >;
    this.busOrderCollection = collection(firestore, Collections.busOrders) as CollectionReference<
      BusOrder,
      BusOrder
    >;
    this.festivalEditionCollection = collection(
      firestore,
      Collections.festivalEditions,
    ) as CollectionReference<FestivalEdition, FestivalEdition>;
    this.eventCollection = collection(firestore, Collections.events) as CollectionReference<
      Event,
      Event
    >;
  }

  async getAppData() {
    const ticketingDataDoc = await getDoc(doc(collection(firestore, 'app'), 'ticketing'));
    const ticketingData = ticketingDataDoc.data() as {
      ticketsSold: number;
      ticketsAvailable: number;
    };

    return ticketingData;
  }

  async getActiveFestivalEdition(): Promise<FestivalEdition | null> {
    const configDoc = await getDoc(
      doc(collection(firestore, Collections.appData), 'festivalEditions'),
    );
    const activeId = (configDoc.data() as ActiveFestivalConfig | undefined)
      ?.activeFestivalEditionId;
    if (!activeId) return null;
    const editionDoc = await getDoc(doc(this.festivalEditionCollection, activeId));
    return editionDoc.data() ?? null;
  }

  async getEvent(eventId: string): Promise<Event | null> {
    if (!eventId) return null;
    const eventDoc = await getDoc(doc(this.eventCollection, eventId));
    return eventDoc.data() ?? null;
  }

  async getEventBySlug(slug: string): Promise<Event | null> {
    if (!slug) return null;
    const lookupDoc = await getDoc(
      doc(collection(firestore, Collections.appData), `eventSlug:${slug}`),
    );
    const eventId = (lookupDoc.data() as { eventId?: string } | undefined)?.eventId;
    if (!eventId) return null;
    return this.getEvent(eventId);
  }

  async getOwnerTicketTypes(owner: TicketingOwnerRef): Promise<TicketType[]> {
    const constraints =
      owner.kind === 'festivalEdition'
        ? [
            where('owner.kind', '==', 'festivalEdition'),
            where('owner.festivalEditionId', '==', owner.festivalEditionId),
          ]
        : [where('owner.kind', '==', 'event'), where('owner.eventId', '==', owner.eventId)];
    const ticketTypeDocs = await getDocs(query(this.ticketTypeCollection, ...constraints));

    return ticketTypeDocs.docs
      .map((ticketDoc) => ticketDoc.data())
      .filter(
        (ticketType) =>
          !ticketType.archivedAt && (!ticketType.saleState || ticketType.saleState === 'active'),
      )
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  async getPaymentUrl(orderId: string) {
    return getPaymentUrl({ orderId });
  }

  async getTicketTypes(): Promise<TicketType[]> {
    const ticketTypeDocs = await getDocs(query(this.ticketTypeCollection));

    return ticketTypeDocs.docs
      .map((doc) => doc.data())
      .filter((ticketType) => ticketType.owner?.kind !== 'event');
  }

  async getTicket(id: string) {
    try {
      const d = doc(this.ticketCollection, id);
      const result = await getDoc(d);
      return result.data() ?? null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async getOrder(id: string) {
    try {
      const d = doc(this.orderCollection, id);
      const result = await getDoc(d);
      const order = result.data() ?? null;
      if (!order) return null;
      const ticketSnapshots = await Promise.all(
        order.ticketIds.map((ticketId) => {
          return getDoc(doc(this.ticketCollection, ticketId));
        }),
      );
      const tickets = ticketSnapshots.map((doc) => doc.data()).filter(Boolean) as Ticket[];
      return { order, tickets };
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async getHome() {
    try {
      const d = doc(this.contentCollection, 'home');
      const result = await getDoc(d);
      const data = result.data() as { de: string; en: string };
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async getAwareness() {
    try {
      const d = doc(this.contentCollection, 'awareness');
      const result = await getDoc(d);
      const data = result.data() as { de: string; en: string };
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async getProgram() {
    try {
      const d = doc(this.contentCollection, 'program');
      const result = await getDoc(d);
      const data = result.data() as { de: string; en: string };
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async getFaqs() {
    try {
      const d = doc(this.contentCollection, 'faqs');
      const result = await getDoc(d);
      const data = result.data() as { de: FAQ[]; en: FAQ[] };
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async placeOrder(data: PlaceOrderRequest) {
    return placeOrderFn(data);
  }

  async placeTicketOrder(data: PlaceTicketOrderRequest) {
    return placeTicketOrderFn(data);
  }

  async getBusses(): Promise<Bus[]> {
    const busDocs = await getDocs(query(this.busCollection));

    return busDocs.docs.map((doc) => doc.data());
  }

  async getFestivalBusRoutes(festivalEditionId: string): Promise<BusRoute[]> {
    const routeDocs = await getDocs(
      query(
        this.busRouteCollection,
        where('owner.kind', '==', 'festivalEdition'),
        where('owner.festivalEditionId', '==', festivalEditionId),
      ),
    );

    return routeDocs.docs
      .map((doc) => doc.data())
      .filter(
        (route) =>
          !!route.busRouteId &&
          !route.archivedAt &&
          (!route.saleState || route.saleState !== 'hidden'),
      )
      .sort((a, b) => {
        const sortOrder = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        if (sortOrder !== 0) return sortOrder;
        return a.departureTime.localeCompare(b.departureTime);
      })
      .map((route) => ({ ...route, busId: route.busRouteId }));
  }

  async getBusOrder(id: string) {
    const d = doc(this.busOrderCollection, id);
    const result = await getDoc(d);
    const order = result.data() ?? null;
    return order;
  }

  async placeBusOrder(data: PlaceBusOrderRequest) {
    return placeBusOrderFn(data);
  }

  async transferTicket(data: TransferTicketRequest) {
    return transferTicketFn(data);
  }
}
